import React, { memo, useContext, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { cn } from "@/src/lib/utils";

import SelectBoxCheckBox from "./SelectBoxCheckbox";
import { SelectBoxContext } from "./SelectBoxContext";
import { selectBoxItemPath } from "./selectBoxItemPath";
import { navigatorLanguage } from "../../i18n";
import {
  getContentIcon,
  getDurationShort,
  getTranslation,
} from "../../utils/data/Content";
import Spinner from "../utils/Spinner";

import type { DraggableProvided } from "@hello-pangea/dnd";
import type { ChangeEvent, CSSProperties, JSX, KeyboardEvent } from "react";

import type { SelectBoxDisplayItem } from "./selectBox.types";

interface SelectBoxItemProps {
  hasRank?: boolean;
  item: SelectBoxDisplayItem;
  seasonToSerieMap: Map<string, string>;
  source_id: string;
  provided: DraggableProvided;
  style?: CSSProperties;
  isDragging?: boolean;
}

function SelectBoxItem({
  hasRank,
  item,
  seasonToSerieMap,
  source_id,
  provided,
  style,
  isDragging,
}: SelectBoxItemProps): JSX.Element {
  const { t } = useTranslation("editor-utils");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const context = useContext(SelectBoxContext);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    setIsChecked(Boolean(item.isChecked && item.isDraggable));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.isChecked]);

  const itemCount = context.selectedItems.length;
  // Lets the input be emptied while typing; null outside of an edit.
  const [rankDraft, setRankDraft] = useState<string | null>(null);

  const icon = getContentIcon(item);

  const cellClassNames = cn(
    "flex h-full items-center truncate pl-2.5",
    item.isDraggable
      ? "cursor-grab text-muted-foreground"
      : "cursor-not-allowed text-subtle",
  );

  const handleRankChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setRankDraft(event.target.value);

    const typedRank = parseInt(event.target.value, 10);

    if (!Number.isNaN(typedRank)) {
      context.changeSelectedItemRank(item.uuid, typedRank);
    }
  };

  const handleRankBlur = (): void => {
    setRankDraft(null);
    context.endRankEdit();
  };

  const handleRankKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") event.currentTarget.blur();
  };

  const onCheckedItem = (checked: boolean): void => {
    // Nothing changed ?
    if (item.isChecked === checked) return;

    let list = context.multiSelectList;

    // Keep existing behavior: checked state is stored directly on the selectbox item.
    // eslint-disable-next-line no-param-reassign
    item.isChecked = checked;

    if (checked) {
      list.push({
        ...item,
        source_id,
      });
    } else {
      list = context.multiSelectList.filter(
        (element) => element.uuid !== item.uuid,
      );
    }

    setIsChecked(checked);
    context.setMultiSelectList(list);
  };

  const getStyle = ({
    provided: draggableProvided,
    style: overrideStyle,
  }: {
    provided: DraggableProvided;
    style?: CSSProperties;
  }): CSSProperties | undefined => {
    const draggableStyle = draggableProvided.draggableProps.style;

    if (!overrideStyle) {
      return draggableStyle;
    }

    // react-window positions rows with a transform: the drag shift must add to it, not replace it.
    const transform = [overrideStyle.transform, draggableStyle?.transform]
      .filter(Boolean)
      .join(" ");

    return {
      ...overrideStyle,
      ...draggableStyle,
      transform: transform || undefined,
    };
  };

  let { label } = item;

  if (item.type === "season") {
    const translationName = getTranslation(item, navigatorLanguage).name;

    if (translationName) label = `${label} - ${translationName}`;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- ?? would keep an empty label instead of falling back to item.name
  const displayLabel = label || item.name;

  const itemPath = useMemo(
    () =>
      selectBoxItemPath({
        item: {
          itemType: item.itemType,
          type: item.type,
          uuid: item.uuid,
          content_uuid: item.content_uuid,
        },
        projectSlug,
        seasonToSerieMap,
      }),
    [
      item.itemType,
      item.type,
      item.uuid,
      item.content_uuid,
      projectSlug,
      seasonToSerieMap,
    ],
  );

  return (
    <div
      ref={provided.innerRef}
      className={cn(
        "selectbox-item-container border-surface-muted bg-card flex h-12 w-full flex-row border-b last:border-b-0",
        isDragging && "is-dragging",
        !item.isDraggable && "editor-tooltip editor-tooltip-center",
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...provided.draggableProps}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...provided.dragHandleProps}
      style={getStyle({
        provided,
        style,
      })}
    >
      {item.content_uuid ? (
        // Do not display item info when it's not the proper data
        <Spinner />
      ) : (
        <React.Fragment>
          <div className={cn(cellClassNames, "w-1/12 shrink-0")}>
            <span className={`editor-item-status item-status-${item.status}`} />
          </div>
          <div
            className={cn(
              cellClassNames,
              "w-1/12 shrink-0 justify-center pl-0",
            )}
          >
            <i className="material-icons">{icon}</i>
          </div>
          <div className={cn(cellClassNames, "w-17/24 min-w-0")}>
            {itemPath ? (
              <Link
                rel="noopener noreferrer"
                target="_blank"
                title={displayLabel}
                to={itemPath}
                className={cn(
                  "block! cursor-pointer! text-inherit! hover:underline!",
                  "w-full min-w-0 truncate",
                )}
              >
                {displayLabel}
              </Link>
            ) : (
              <span className={cn("block", "w-full min-w-0 truncate")}>
                {displayLabel}
              </span>
            )}
          </div>
          {hasRank ? (
            <div className="text-muted-foreground m-[3px] flex h-[25px] w-1/5 items-center justify-end">
              <input
                className="rank-input mr-[3px] w-[35px]"
                id={`rank-${item.uuid}`}
                max={itemCount}
                min="1"
                onBlur={handleRankBlur}
                onChange={handleRankChange}
                onKeyDown={handleRankKeyDown}
                type="number"
                value={rankDraft ?? item.rank ?? ""}
              />
              / {itemCount}
            </div>
          ) : null}
          <div className={cn(cellClassNames, "w-5/24 shrink-0")}>
            {!!item.duration && getDurationShort(item.duration)}
          </div>
          <div
            className={cn(
              cellClassNames,
              "w-1/12 min-w-[25px] shrink-0 justify-center pl-0",
            )}
          >
            <SelectBoxCheckBox
              canCheck={item.isDraggable}
              isChecked={isChecked}
              name={`selectbox-checkbox-${item.uuid}`}
              onCheckedChange={onCheckedItem}
            />
          </div>
          {!item.isDraggable && (
            <span className="editor-tooltiptext">
              {item.free
                ? t("editor-offers:cantAddFree")
                : t("editor-offers:cantAddOffer")}
            </span>
          )}
        </React.Fragment>
      )}
    </div>
  );
}

export default memo(SelectBoxItem);
